import { autoResizeElementsCommand } from '@blocksuite/affine-block-surface';
import { toast } from '@blocksuite/affine-components/toast';
import {
  type AttachmentBlockProps,
  type ImageBlockModel,
  type ImageBlockProps,
  ImageBlockSchema,
} from '@blocksuite/affine-model';
import {
  copyBlockIndexReviewSubject,
  EditPropsStore,
  FileSizeLimitProvider,
  NativeClipboardProvider,
} from '@blocksuite/affine-shared/services';
import {
  convertToPng,
  formatSize,
  getBlockProps,
  heicDecode,
  isHeicMime,
  isInsidePageEditor,
  readImageSize,
  resolveBlobDisplayName,
  stageBlob,
  transformModel,
  withTempBlobData,
} from '@blocksuite/affine-shared/utils';
import { Bound, type IVec, Vec } from '@blocksuite/global/gfx';
import {
  BlockSelection,
  type BlockStdScope,
  isGfxBlockComponent,
} from '@blocksuite/std';
import { GfxControllerIdentifier } from '@blocksuite/std/gfx';
import type { BlockModel } from '@blocksuite/store';
import type { Signal } from '@preact/signals-core';

import {
  SURFACE_IMAGE_CARD_HEIGHT,
  SURFACE_IMAGE_CARD_WIDTH,
} from './components/image-block-fallback';
import type { ImageBlockComponent } from './image-block';
import { createDroppedImageAppearance } from './image-drop-appearance';
import type { ImageEdgelessBlockComponent } from './image-edgeless-block';
import { getImagePresentationSize } from './image-presentation-geometry';

const IMAGE_MIME_SNIFF_BYTES = 4100;
const NATIVE_CLIPBOARD_INLINE_MAX_BYTES = 4 * 1024 * 1024;

/**
 * sl-luoo: Extract a short format label from MIME type (e.g. "JPG", "PNG", "HEIC").
 */
export function getFormatFromMime(mime: string | undefined): string | null {
  if (!mime) return null;
  if (mime === 'application/octet-stream') return null;
  const sub = mime.split('/')[1]?.toUpperCase();
  if (!sub) return null;
  if (sub === 'JPEG') return 'JPG';
  if (sub === 'SVG+XML') return 'SVG';
  if (sub === 'VND.MICROSOFT.ICON') return 'ICO';
  return sub;
}

export async function normalizeImageBlobMime(blob: Blob): Promise<Blob> {
  if (blob.type && blob.type !== 'application/octet-stream') {
    return blob;
  }

  const header = await blob.slice(0, IMAGE_MIME_SNIFF_BYTES).arrayBuffer();
  const FileType = await import('file-type');
  const fileType = await FileType.fileTypeFromBuffer(new Uint8Array(header));

  return fileType?.mime ? new Blob([blob], { type: fileType.mime }) : blob;
}

/** Block components that expose an imageFormat$ signal for badge display. */
interface FormatAwareBlock {
  imageFormat$?: Signal<string | null>;
}

async function persistMissingImageLayoutProps(
  block: ImageBlockComponent | ImageEdgelessBlockComponent,
  blob: Blob,
  isCurrent: () => boolean
) {
  if (block.store.readonly) return;

  const { model } = block;
  let imageSize: { width: number; height: number } | undefined;
  if (!model.props.width || !model.props.height) {
    try {
      imageSize = await readImageSize(blob);
    } catch {
      // Keep render path alive for malformed or browser-unsupported blobs.
    }
  }
  // Source changes, unmounts and user edits may happen during image decoding.
  if (!isCurrent() || block.store.readonly) return;
  const props: Partial<ImageBlockProps> = {};
  const currentSize = model.props.size;

  if (
    !model.props.type?.startsWith('image/') &&
    blob.type.startsWith('image/')
  ) {
    props.type = blob.type;
  }

  if (typeof currentSize !== 'number' || currentSize < 0) {
    props.size = blob.size;
  }

  if (imageSize && imageSize.width > 0 && imageSize.height > 0) {
    if (!model.props.width) props.width = imageSize.width;
    if (!model.props.height) props.height = imageSize.height;
  }

  if (Object.keys(props).length > 0) {
    block.store.withoutTransact(() => {
      block.store.updateBlock(model, props);
    });
  }
}

type ImageRefresh = {
  sourceId: string | undefined;
  disposables: ImageBlockComponent['disposables'];
  pending: Promise<void> | null;
};

const imageRefreshes = new WeakMap<
  ImageBlockComponent | ImageEdgelessBlockComponent,
  ImageRefresh
>();

async function getImageBlob(model: ImageBlockModel) {
  const sourceId = model.props.sourceId$.peek();
  if (!sourceId) return null;

  const doc = model.store;
  let blob = await doc.blobSync.get(sourceId);
  if (!blob) return null;

  blob = await normalizeImageBlobMime(blob);

  if (!blob.type.startsWith('image/')) return null;

  return blob;
}

/**
 * Refresh the image block's display URL.
 *
 * sl-luoo: For HEIC/HEIF blobs, Chrome cannot render them natively.
 * We decode them client-side via heic-to (WASM) and set a JPEG blob URL directly.
 * During decode, the ResourceController shows a loading spinner (downloading: true).
 */
export function refreshData(
  block: ImageBlockComponent | ImageEdgelessBlockComponent
): Promise<void> {
  const sourceId = block.model.props.sourceId$.peek();
  const disposables = block.disposables;
  const previous = imageRefreshes.get(block);
  if (
    previous?.pending &&
    previous.sourceId === sourceId &&
    previous.disposables === disposables &&
    !disposables.disposed
  ) {
    return previous.pending;
  }

  // Share only an in-flight refresh in this component lifetime. No image or
  // editor survives its owning permission-guarded view through this map.
  const request: ImageRefresh = { sourceId, disposables, pending: null };
  imageRefreshes.set(block, request);
  const pending = refreshImageData(block, request).finally(() => {
    if (imageRefreshes.get(block) === request) imageRefreshes.delete(block);
  });
  request.pending = pending;
  return pending;
}

async function refreshImageData(
  block: ImageBlockComponent | ImageEdgelessBlockComponent,
  request: ImageRefresh
) {
  const rc = block.resourceController;
  const { sourceId, disposables } = request;
  const isCurrent = () =>
    block.isConnected &&
    !disposables.disposed &&
    block.disposables === disposables &&
    imageRefreshes.get(block) === request &&
    block.model.props.sourceId$.peek() === sourceId;

  // The viewport schedules visible canvas hosts first. Start their blob reads
  // after that first render instead of starting every offscreen image at mount.
  if (isGfxBlockComponent(block) && !block.hasUpdated) {
    await block.updateComplete;
  }
  if (!isCurrent()) return;

  // sl-luoo: Show loading state immediately so the fallback card renders
  // a spinner instead of an error while we fetch + detect the blob MIME.
  rc.updateState({ downloading: true });

  let blob = await rc.blob();
  if (!isCurrent()) return;

  // sl-luoo: OPFS and some blob sources return blobs without a precise MIME
  // type. Sniff magic bytes so HEIC detection and the format badge stay
  // correct instead of showing generic "OCTET-STREAM".
  if (blob) blob = await normalizeImageBlobMime(blob);
  if (!isCurrent()) return;

  if (blob) {
    await persistMissingImageLayoutProps(block, blob, isCurrent);
  }
  if (!isCurrent()) return;

  // sl-luoo: Set image format for badge overlay on canvas/page blocks.
  const formatBlock = block as unknown as FormatAwareBlock;
  if (formatBlock.imageFormat$) {
    formatBlock.imageFormat$.value = getFormatFromMime(blob?.type);
  }

  if (blob && isHeicMime(blob.type)) {
    // Chrome cannot decode HEIC natively — decode via heic-to to JPEG.
    try {
      const { jpeg } = await heicDecode(blob);
      if (isCurrent()) rc.refreshUrlFromBlob(jpeg, sourceId);
    } catch (error) {
      if (!isCurrent()) return;
      console.error('[sl-luoo] HEIC browser decode failed:', error);
      rc.refreshUrlFromBlob(blob, sourceId);
    } finally {
      if (isCurrent()) rc.updateState({ downloading: false });
    }
    return;
  }

  // Reuse the normalized blob, including its detected MIME; do not read OPFS
  // again just to create the display URL. Missing-blob errors remain intact.
  if (blob) rc.refreshUrlFromBlob(blob, sourceId);
  rc.updateState({ downloading: false });
}

export async function downloadImageBlob(
  block: ImageBlockComponent | ImageEdgelessBlockComponent
) {
  const { host, blobUrl, resourceController } = block;

  if (!blobUrl) {
    toast(host, 'Failed to download image!');
    return;
  }

  if (resourceController.state$.peek().downloading) {
    toast(host, 'Download in progress...');
    return;
  }

  resourceController.updateState({ downloading: true });

  toast(host, 'Downloading image...');

  const tmpLink = document.createElement('a');
  const event = new MouseEvent('click');
  tmpLink.download = 'image';
  tmpLink.href = blobUrl;
  tmpLink.dispatchEvent(event);
  tmpLink.remove();

  resourceController.updateState({ downloading: false });
}

export async function resetImageSize(
  block: ImageBlockComponent | ImageEdgelessBlockComponent
) {
  const { model } = block;

  const blob = await getImageBlob(model);
  if (!blob) {
    console.error('Failed to get image blob');
    return;
  }

  const imageSize = await readImageSize(blob);

  const bound = model.elementBound;
  bound.w = imageSize.width;
  bound.h = imageSize.height;

  const xywh = bound.serialize();
  const props: Partial<ImageBlockProps> = { ...imageSize, xywh };

  block.store.updateBlock(model, props);
}

export async function copyImageBlob(
  block: ImageBlockComponent | ImageEdgelessBlockComponent
) {
  const { host, model, std } = block;
  let blob = await getImageBlob(model);
  if (!blob) {
    console.error('Failed to get image blob');
    return;
  }

  let copied = false;

  try {
    // Copies the image as PNG in Electron.
    const copyAsPNG = std.getOptional(NativeClipboardProvider)?.copyAsPNG;
    // Electron IPC accepts ArrayBuffer only; keep this explicit user export
    // bounded so large Canvas image blobs stay on the Blob clipboard path.
    if (copyAsPNG && blob.size <= NATIVE_CLIPBOARD_INLINE_MAX_BYTES) {
      copied = await copyAsPNG(await blob.arrayBuffer());
    }

    // The current clipboard only supports the `image/png` image format.
    // The `ClipboardItem.supports('image/svg+xml')` is not currently used,
    // because when pasting, the content is not read correctly.
    //
    // https://developer.mozilla.org/en-US/docs/Web/API/ClipboardItem
    // https://alexharri.com/blog/clipboard
    if (!copied) {
      if (blob.type !== 'image/png') {
        blob = await convertToPng(blob);
        if (!blob) {
          console.error('Failed to convert blob to PNG');
          return;
        }
      }

      if (!globalThis.isSecureContext) {
        console.error(
          'Clipboard API is not available in insecure context',
          blob.type,
          blob
        );
        return;
      }

      await navigator.clipboard.write([
        new ClipboardItem({ [blob.type]: blob }),
      ]);
    }

    toast(host, 'Copied image to clipboard');
  } catch (error) {
    console.error(error);
  }
}

/**
 * Turn the image block into a attachment block.
 */
export async function turnImageIntoCardView(
  block: ImageBlockComponent | ImageEdgelessBlockComponent
) {
  const doc = block.store;
  if (!doc.schema.flavourSchemaMap.has('affine:attachment')) {
    console.error('The attachment flavour is not supported!');
    return;
  }

  const model = block.model;
  const sourceId = model.props.sourceId$.peek();
  const blob = await getImageBlob(model);
  if (!sourceId || !blob) {
    console.error('Image data not available');
    return;
  }

  const { saveImageData } = withTempBlobData();
  saveImageData(sourceId, {
    width: model.props.width,
    height: model.props.height,
  });
  const attachmentProp: Partial<AttachmentBlockProps> = {
    sourceId,
    name: resolveBlobDisplayName({
      occurrenceName: model.props.name,
      fallbackName: 'image',
      mimeType: model.props.type || blob.type,
    }),
    size: blob.size,
    type: model.props.type || blob.type,
    caption: model.props.caption,
  };
  transformModel(model, 'affine:attachment', attachmentProp);
}

export function shouldResizeImage(node: Node, target: EventTarget | null) {
  return !!(
    target &&
    target instanceof HTMLElement &&
    node.contains(target) &&
    target.classList.contains('resize')
  );
}

function hasExceeded(
  std: BlockStdScope,
  files: File[],
  maxFileSize = std.get(FileSizeLimitProvider).maxFileSize
) {
  const exceeded = files.some(file => file.size > maxFileSize);

  if (exceeded) {
    const size = formatSize(maxFileSize);
    toast(std.host, `You can only upload files less than ${size}`);
  }

  return exceeded;
}

/**
 * Build image props after durable local staging. The sourceId exists before
 * the block enters the document, so the image can render while offline.
 */
async function buildPropsInstant(
  std: BlockStdScope,
  file: File
): Promise<Partial<ImageBlockProps>> {
  const { size } = file;
  const imageSize = await readImageSize(file);

  if (!(imageSize.width * imageSize.height)) {
    toast(std.host, 'Failed to read image size, please try another image');
    throw new Error('Failed to read image size');
  }

  const sourceId = await stageBlob(std, file);
  return {
    name: file.name,
    size,
    sourceId,
    type: file.type,
    ...imageSize,
  } satisfies Partial<ImageBlockProps>;
}

export async function addSiblingImageBlocks(
  std: BlockStdScope,
  files: File[],
  targetModel: BlockModel,
  placement: 'after' | 'before' = 'after'
) {
  files = files.filter(file => file.type.startsWith('image/'));
  if (!files.length) return [];

  if (hasExceeded(std, files)) return [];

  const flavour = ImageBlockSchema.model.flavour;

  const instantResults = await Promise.all(
    files.map(file => buildPropsInstant(std, file))
  );

  const blockIds = std.store.addSiblingBlocks(
    targetModel,
    instantResults.map(props => ({ ...props, flavour })),
    placement
  );

  return blockIds;
}

export async function addImageBlocks(
  std: BlockStdScope,
  files: File[],
  parent?: BlockModel | string | null,
  parentIndex?: number
) {
  files = files.filter(file => file.type.startsWith('image/'));
  if (!files.length) return [];

  if (hasExceeded(std, files)) return [];

  const flavour = ImageBlockSchema.model.flavour;

  const instantResults = await Promise.all(
    files.map(file => buildPropsInstant(std, file))
  );

  const blocks = instantResults.map(props => ({
    flavour,
    blockProps: props,
  }));

  const blockIds = std.store.addBlocks(blocks, parent, parentIndex);

  return blockIds;
}

export async function addImages(
  std: BlockStdScope,
  files: File[],
  options: {
    point?: IVec;
    maxWidth?: number;
    shouldTransformPoint?: boolean; // determines whether we should use `toModelCoord` to convert the point
  }
): Promise<string[]> {
  files = files.filter(file => file.type.startsWith('image/'));
  if (!files.length) return [];

  if (hasExceeded(std, files)) return [];

  const flavour = ImageBlockSchema.model.flavour;

  const instantResults = await Promise.all(
    files.map(file => buildPropsInstant(std, file))
  );

  const gfx = std.get(GfxControllerIdentifier);
  const isMultiple = instantResults.length > 1;
  const inTopLeft = isMultiple;
  const gap = 32;
  const { point, maxWidth, shouldTransformPoint = true } = options;

  let { x, y } = gfx.viewport.center;
  if (point) {
    if (shouldTransformPoint) {
      [x, y] = gfx.viewport.toModelCoord(...point);
    } else {
      [x, y] = point;
    }
  }

  const xy = [x, y];

  const blocks = instantResults.map((props, i) => {
    if (maxWidth) {
      const p = props.height! / props.width!;
      props.width = Math.min(props.width!, maxWidth);
      props.height = props.width * p;
    }

    const { width, height } = props;
    const inheritedProps = std.get(EditPropsStore).applyLastProps('image', {});
    const appearance = createDroppedImageAppearance(
      inheritedProps.presentation
    );
    const presentationSize = getImagePresentationSize(
      width!,
      height!,
      appearance.presentation
    );
    const center = Vec.addScalar(xy, i * gap);
    const index = gfx.layer.generateIndex();
    const xywh = calcBoundByOrigin(
      center,
      inTopLeft,
      presentationSize.width,
      presentationSize.height
    ).serialize();

    const blockProps = {
      ...inheritedProps,
      ...props,
      width,
      height,
      xywh,
      index,
    };
    return {
      flavour,
      blockProps: {
        ...blockProps,
        ...appearance,
      },
    };
  });

  const blockIds = std.store.addBlocks(blocks, gfx.surface);

  gfx.selection.set({
    elements: blockIds,
    editing: false,
  });

  if (isMultiple) {
    std.command.exec(autoResizeElementsCommand);
  }

  return blockIds;
}

export function calcBoundByOrigin(
  point: IVec,
  inTopLeft = false,
  width = SURFACE_IMAGE_CARD_WIDTH,
  height = SURFACE_IMAGE_CARD_HEIGHT
) {
  return inTopLeft
    ? new Bound(point[0], point[1], width, height)
    : Bound.fromCenter(point, width, height);
}

export function duplicate(block: ImageBlockComponent) {
  const model = block.model;
  const blockProps = getBlockProps(model);
  const {
    width: _width,
    height: _height,
    xywh: _xywh,
    rotate: _rotate,
    zIndex: _zIndex,
    ...duplicateProps
  } = blockProps;

  const { store } = model;
  const parent = store.getParent(model);
  if (!parent) {
    console.error(`Parent not found for block(${model.flavour}) ${model.id}`);
    return;
  }

  const index = parent?.children.indexOf(model);
  const duplicateId = store.addBlock(
    model.flavour,
    duplicateProps,
    parent,
    index + 1
  );
  copyBlockIndexReviewSubject({
    std: block.std,
    subjectKind: 'image-block',
    sourceBlockId: model.id,
    targetBlockId: duplicateId,
    blobId: model.props.sourceId$?.value ?? model.props.sourceId,
  });

  const editorHost = block.host;
  editorHost.updateComplete
    .then(() => {
      const { selection } = editorHost;
      selection.setGroup('note', [
        selection.create(BlockSelection, {
          blockId: duplicateId,
        }),
      ]);
      if (isInsidePageEditor(editorHost)) {
        const duplicateElement = editorHost.view.getBlock(duplicateId);
        if (duplicateElement) {
          duplicateElement.scrollIntoView(true);
        }
      }
    })
    .catch(console.error);
}
